import {
  ArrowRight,
  CheckCircle,
  Clock,
  DollarSign,
  Minus,
  XCircle,
} from 'lucide-react';
import { motion } from 'motion/react';

import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Competitor comparison data
const competitorData = {
  servicenow: {
    name: 'ServiceNow',
    fullName: 'ServiceNow GRC & SecOps',
    category: 'GRC & ITSM Platform',
    color: 'from-green-500 to-green-600',
    bgGradient: 'from-green-900 via-green-800 to-slate-900',
    description: 'ServiceNow is an enterprise platform for IT service management with GRC and security operations modules.',
    strengths: ['Strong ITSM capabilities', 'Large enterprise ecosystem', 'Workflow automation'],
    weaknesses: ['High cost of ownership', 'Complex implementation', 'Requires extensive customization', 'Separate modules require integration'],
    bosssAdvantages: [
      'Unified platform - no module silos',
      '70% lower total cost of ownership',
      'Weeks to deploy vs. 12-18 months',
      'Built-in AI agents for automation',
      'Modern, intuitive interface',
    ],
    features: [
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Compliance Automation', bosss: true, competitor: 'partial' },
      { category: 'GRC', feature: 'Policy Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Audit Management', bosss: true, competitor: true },
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: 'addon' },
      { category: 'IAM', feature: 'Access Reviews', bosss: true, competitor: 'addon' },
      { category: 'IAM', feature: 'Provisioning Automation', bosss: true, competitor: 'addon' },
      { category: 'CMDB', feature: 'Asset Discovery', bosss: true, competitor: true },
      { category: 'CMDB', feature: 'Dependency Mapping', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Threat Detection', bosss: true, competitor: 'addon' },
      { category: 'XDR', feature: 'Incident Response', bosss: true, competitor: true },
      { category: 'XDR', feature: 'MITRE ATT&CK Mapping', bosss: true, competitor: 'partial' },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Unified Dashboard', bosss: true, competitor: 'partial' },
      { category: 'Platform', feature: 'Multi-tenant', bosss: true, competitor: true },
    ],
    pricing: {
      bosss: 'Transparent per-user pricing, all modules included',
      competitor: 'Per-module licensing, implementation fees, ongoing consulting costs',
    },
    deployment: {
      bosss: '2-4 weeks average deployment',
      competitor: '12-18 months typical enterprise deployment',
    },
  },
  splunk: {
    name: 'Splunk',
    fullName: 'Splunk Enterprise Security',
    category: 'SIEM & Security Analytics',
    color: 'from-orange-500 to-orange-600',
    bgGradient: 'from-orange-900 via-orange-800 to-slate-900',
    description: 'Splunk is a data analytics platform with security information and event management (SIEM) capabilities.',
    strengths: ['Powerful log analytics', 'Flexible data ingestion', 'Large app ecosystem'],
    weaknesses: ['High data volume costs', 'Complex SPL query language', 'No built-in GRC or IAM', 'Requires significant expertise'],
    bosssAdvantages: [
      'GRC, IAM, CMDB included - not just SIEM',
      'Predictable pricing without data volume penalties',
      'No query language required for common tasks',
      'Pre-built compliance frameworks',
      'AI-powered automation reduces analyst burden',
    ],
    features: [
      { category: 'XDR', feature: 'Log Management', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Threat Detection', bosss: true, competitor: true },
      { category: 'XDR', feature: 'SIEM Correlation', bosss: true, competitor: true },
      { category: 'XDR', feature: 'SOAR Integration', bosss: true, competitor: 'addon' },
      { category: 'XDR', feature: 'MITRE ATT&CK Mapping', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Compliance Automation', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Policy Management', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Access Reviews', bosss: true, competitor: false },
      { category: 'CMDB', feature: 'Asset Discovery', bosss: true, competitor: 'partial' },
      { category: 'CMDB', feature: 'Configuration Tracking', bosss: true, competitor: false },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Unified Dashboard', bosss: true, competitor: 'partial' },
      { category: 'Platform', feature: 'No Query Language Required', bosss: true, competitor: false },
    ],
    pricing: {
      bosss: 'Predictable per-user pricing',
      competitor: 'Ingestion-based pricing - costs scale with data volume',
    },
    deployment: {
      bosss: '2-4 weeks with all capabilities',
      competitor: '3-6 months for enterprise SIEM deployment',
    },
  },
  crowdstrike: {
    name: 'CrowdStrike',
    fullName: 'CrowdStrike Falcon Platform',
    category: 'Endpoint Security & XDR',
    color: 'from-red-500 to-red-600',
    bgGradient: 'from-red-900 via-red-800 to-slate-900',
    description: 'CrowdStrike is a cloud-native endpoint protection platform with XDR capabilities.',
    strengths: ['Strong endpoint protection', 'Cloud-native architecture', 'Fast deployment'],
    weaknesses: ['No GRC capabilities', 'No identity governance', 'Limited CMDB', 'Focused only on security operations'],
    bosssAdvantages: [
      'Complete platform: GRC + IAM + CMDB + XDR',
      'Compliance automation included',
      'Identity governance built-in',
      'Asset management and CMDB',
      'Lower total cost for full security program',
    ],
    features: [
      { category: 'XDR', feature: 'Endpoint Detection', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Threat Intelligence', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Incident Response', bosss: true, competitor: true },
      { category: 'XDR', feature: 'MITRE ATT&CK Mapping', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Network Detection', bosss: true, competitor: 'partial' },
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Compliance Frameworks', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Audit Management', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Access Reviews', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Privileged Access', bosss: true, competitor: 'addon' },
      { category: 'CMDB', feature: 'Asset Inventory', bosss: true, competitor: 'partial' },
      { category: 'CMDB', feature: 'Dependency Mapping', bosss: true, competitor: false },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Multi-tenant', bosss: true, competitor: true },
    ],
    pricing: {
      bosss: 'All-inclusive platform pricing',
      competitor: 'Per-endpoint pricing + module add-ons',
    },
    deployment: {
      bosss: '2-4 weeks full platform',
      competitor: 'Days for endpoint, months for full XDR',
    },
  },
  sailpoint: {
    name: 'SailPoint',
    fullName: 'SailPoint IdentityNow',
    category: 'Identity Governance',
    color: 'from-blue-500 to-blue-600',
    bgGradient: 'from-blue-900 via-blue-800 to-slate-900',
    description: 'SailPoint is an identity governance platform for managing user access and compliance.',
    strengths: ['Deep identity analytics', 'Strong access certification', 'Broad connector library'],
    weaknesses: ['No security operations', 'No GRC platform', 'No CMDB', 'Identity-only focus'],
    bosssAdvantages: [
      'IAM plus GRC, CMDB, and XDR in one platform',
      'Security context for identity decisions',
      'Compliance automation beyond identity',
      'Asset-aware access management',
      'Integrated threat detection',
    ],
    features: [
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: true },
      { category: 'IAM', feature: 'Access Certification', bosss: true, competitor: true },
      { category: 'IAM', feature: 'Provisioning', bosss: true, competitor: true },
      { category: 'IAM', feature: 'Role Mining', bosss: true, competitor: true },
      { category: 'IAM', feature: 'SoD Analysis', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Compliance Frameworks', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Policy Management', bosss: true, competitor: false },
      { category: 'XDR', feature: 'Threat Detection', bosss: true, competitor: false },
      { category: 'XDR', feature: 'Incident Response', bosss: true, competitor: false },
      { category: 'XDR', feature: 'SIEM Integration', bosss: true, competitor: 'partial' },
      { category: 'CMDB', feature: 'Asset Management', bosss: true, competitor: false },
      { category: 'CMDB', feature: 'Application Inventory', bosss: true, competitor: 'partial' },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Unified Dashboard', bosss: true, competitor: false },
    ],
    pricing: {
      bosss: 'Platform includes all capabilities',
      competitor: 'Per-identity pricing, separate modules',
    },
    deployment: {
      bosss: '2-4 weeks full platform',
      competitor: '6-12 months enterprise deployment',
    },
  },
  archer: {
    name: 'Archer',
    fullName: 'RSA Archer GRC',
    category: 'GRC Platform',
    color: 'from-purple-500 to-purple-600',
    bgGradient: 'from-purple-900 via-purple-800 to-slate-900',
    description: 'RSA Archer is a legacy enterprise GRC platform for risk management and compliance.',
    strengths: ['Mature GRC platform', 'Customizable workflows', 'Large enterprise installed base'],
    weaknesses: ['Legacy architecture', 'Complex administration', 'Slow innovation', 'No modern security operations'],
    bosssAdvantages: [
      'Modern cloud-native architecture',
      'Integrated security operations (XDR)',
      'Identity governance included',
      'AI-powered automation',
      'Fraction of the implementation time',
    ],
    features: [
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Compliance Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Policy Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Audit Management', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Vendor Risk', bosss: true, competitor: true },
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Access Reviews', bosss: true, competitor: false },
      { category: 'XDR', feature: 'Threat Detection', bosss: true, competitor: false },
      { category: 'XDR', feature: 'Incident Response', bosss: true, competitor: 'partial' },
      { category: 'XDR', feature: 'Security Monitoring', bosss: true, competitor: false },
      { category: 'CMDB', feature: 'Asset Management', bosss: true, competitor: 'partial' },
      { category: 'CMDB', feature: 'Discovery', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Cloud-Native', bosss: true, competitor: false },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Modern UI', bosss: true, competitor: false },
    ],
    pricing: {
      bosss: 'Modern SaaS pricing model',
      competitor: 'Legacy licensing + heavy services costs',
    },
    deployment: {
      bosss: '2-4 weeks',
      competitor: '12-24 months typical',
    },
  },
  qualys: {
    name: 'Qualys',
    fullName: 'Qualys Cloud Platform',
    category: 'Vulnerability Management',
    color: 'from-cyan-500 to-cyan-600',
    bgGradient: 'from-cyan-900 via-cyan-800 to-slate-900',
    description: 'Qualys is a cloud security platform focused on vulnerability management and scanning.',
    strengths: ['Strong vulnerability scanning', 'Cloud-native', 'Asset discovery'],
    weaknesses: ['No GRC platform', 'No identity governance', 'Limited incident response', 'Vulnerability-focused'],
    bosssAdvantages: [
      'Full security platform beyond vulnerability scanning',
      'GRC and compliance automation',
      'Identity governance included',
      'Integrated incident response',
      'AI-powered risk prioritization',
    ],
    features: [
      { category: 'XDR', feature: 'Vulnerability Scanning', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Asset Discovery', bosss: true, competitor: true },
      { category: 'XDR', feature: 'Threat Detection', bosss: true, competitor: 'partial' },
      { category: 'XDR', feature: 'Incident Response', bosss: true, competitor: 'partial' },
      { category: 'XDR', feature: 'Web App Scanning', bosss: true, competitor: true },
      { category: 'GRC', feature: 'Risk Management', bosss: true, competitor: false },
      { category: 'GRC', feature: 'Compliance Frameworks', bosss: true, competitor: 'partial' },
      { category: 'GRC', feature: 'Policy Management', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Identity Governance', bosss: true, competitor: false },
      { category: 'IAM', feature: 'Access Reviews', bosss: true, competitor: false },
      { category: 'CMDB', feature: 'Asset Management', bosss: true, competitor: true },
      { category: 'CMDB', feature: 'Configuration Assessment', bosss: true, competitor: true },
      { category: 'Platform', feature: 'AI Voice Agents', bosss: true, competitor: false },
      { category: 'Platform', feature: 'Unified Dashboard', bosss: true, competitor: 'partial' },
      { category: 'Platform', feature: 'Multi-tenant', bosss: true, competitor: true },
    ],
    pricing: {
      bosss: 'Unified platform pricing',
      competitor: 'Per-asset/IP pricing, module add-ons',
    },
    deployment: {
      bosss: '2-4 weeks full platform',
      competitor: 'Weeks for VM, months for full platform',
    },
  },
};

/**
 * A single self-contained, citable summary sentence for AI engines/crawlers
 * that only read the first paragraph (or quote one passage out of context).
 * Built entirely from each competitor's existing bosssAdvantages/pricing/
 * deployment data above -- not new copy -- so it can't drift from or
 * contradict the rest of the page.
 */
function FeatureIcon({ value }) {
  if (value === true) {
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  }
  else if (value === false) {
    return <XCircle className="w-5 h-5 text-red-400" />;
  }
  else if (value === 'partial') {
    return <Minus className="w-5 h-5 text-yellow-500" />;
  }
  else if (value === 'addon') {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">Add-on</span>
    );
  }
  return <Minus className="w-5 h-5 text-gray-300" />;
}

export function CompareDetail({ competitorId }) {
  const onNavigateToTop = useNavigateToTop();
  const competitor = competitorData[competitorId];

  if (!competitor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Comparison Not Found</h1>
          <Button onClick={() => onNavigateToTop('/compare')}>View All Comparisons</Button>
        </div>
      </div>
    );
  }

  // Group features by category
  const featuresByCategory = competitor.features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`relative py-24 bg-gradient-to-br ${competitor.bgGradient} overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20">
              <span className="text-white">{competitor.category}</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              BOSSS vs.
              {' '}
              {competitor.name}
            </h1>

            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-6">
              {competitor.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-semibold"
                onClick={() => onNavigateToTop('/demo')}
              >
                See BOSSS in Action
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="px-8 py-4 border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 font-semibold backdrop-blur-sm"
                onClick={() => onNavigateToTop('/compare')}
              >
                View All Comparisons
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path={`/compare/${competitorId}`} />

      {/* Why Switch Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* BOSSS Advantages */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900">Why Choose BOSSS</h2>
              </div>

              <ul className="space-y-4">
                {competitor.bosssAdvantages.map((advantage, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{advantage}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Competitor Limitations */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${competitor.color} flex items-center justify-center`}>
                  <span className="text-white font-bold text-lg">{competitor.name.charAt(0)}</span>
                </div>

                <h2 className="text-2xl font-bold text-gray-900">
                  {competitor.name}
                  {' '}
                  Limitations
                </h2>
              </div>

              <ul className="space-y-4">
                {competitor.weaknesses.map((weakness, index) => (
                  <li key={index} className="flex items-start">
                    <XCircle className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{weakness}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats Comparison */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Pricing */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-6 h-6 text-green-500" />
                    <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-sm font-medium text-green-800">BOSSS</p>
                      <p className="text-sm text-green-700">{competitor.pricing.bosss}</p>
                    </div>

                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-sm font-medium text-gray-700">{competitor.name}</p>
                      <p className="text-sm text-gray-600">{competitor.pricing.competitor}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Deployment */}
            <motion.div
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-blue-500" />
                    <h3 className="text-lg font-semibold text-gray-900">Deployment Time</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">BOSSS</p>
                      <p className="text-sm text-blue-700">{competitor.deployment.bosss}</p>
                    </div>

                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-sm font-medium text-gray-700">{competitor.name}</p>
                      <p className="text-sm text-gray-600">{competitor.deployment.competitor}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Feature-by-Feature Comparison
            </h2>

            <p className="text-gray-600">
              <span className="inline-flex items-center mr-4">
                <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                {' '}
                Included
              </span>

              <span className="inline-flex items-center mr-4">
                <Minus className="w-4 h-4 text-yellow-500 mr-1" />
                {' '}
                Partial
              </span>

              <span className="inline-flex items-center mr-4">
                <XCircle className="w-4 h-4 text-red-400 mr-1" />
                {' '}
                Not Available
              </span>

              <span className="inline-flex items-center">
                <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium mr-1">Add-on</span>
                {' '}
                Extra Cost
              </span>
            </p>
          </motion.div>

          {Object.entries(featuresByCategory).map(([category, features]) => (
            <motion.div
              key={category}
              className="mb-8"
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 px-4">{category}</h3>

              <div className="bg-slate-50 rounded-xl overflow-hidden">
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-100 font-medium text-sm text-gray-700">
                  <div>Feature</div>
                  <div className="text-center">BOSSS</div>
                  <div className="text-center">{competitor.name}</div>
                </div>

                {features.map((feature, index) => (
                  <div
                    key={feature.feature}
                    className={`grid grid-cols-3 gap-4 p-4 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                  >
                    <div className="text-gray-700">{feature.feature}</div>

                    <div className="flex justify-center">
                      <FeatureIcon value={feature.bosss} />
                    </div>

                    <div className="flex justify-center">
                      <FeatureIcon value={feature.competitor} />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 bg-gradient-to-br ${competitor.bgGradient}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Switch from
              {' '}
              {competitor.name}
              ?
            </h2>

            <p className="text-xl text-white/80 mb-8">
              See how BOSSS can consolidate your security stack and reduce complexity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-semibold"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule a Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="px-8 py-4 border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 font-semibold backdrop-blur-sm"
                onClick={() => onNavigateToTop('/contact')}
              >
                Talk to Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// Export competitor IDs for routing
export const competitorIds = Object.keys(competitorData);

export default CompareDetail;
